import * as React from "react";
import { Previous } from "./Previous";
import { Next } from "./Next";

export interface IPaginationProps {
  steps: number;
  onStepChangeCb: (step: number) => void;
  previousLabel: string;
  nextLabel: string;
  activeStep: number;
  showPreviousOnFirstPage: boolean;
  showNextOnLastPage: boolean;
  visiblePages?: number;
  showLastPage?: boolean;
  alwaysShowFirstPage?: boolean;
}

/**
 * Renders page numbers of a [[ContentPagination]]
 */
export class Pagination extends React.PureComponent<IPaginationProps> {
  constructor(props: IPaginationProps) {
    super(props);
  }
  static defaultProps = {
    visiblePages: 10,
    showLastPage: false,
    alwaysShowFirstPage: false,
  };
  private onPrevious = (e: React.MouseEvent) => {
    e.preventDefault();
    const newStep = this.props.activeStep - 1;
    this.props.onStepChangeCb(newStep);
  };

  private onNext = (e: React.MouseEvent) => {
    e.preventDefault();
    const newStep = this.props.activeStep + 1;
    this.props.onStepChangeCb(newStep);
  };

  private onChangeStep = (step: number, e: React.MouseEvent) => {
    e.preventDefault();
    this.props.onStepChangeCb(step);
  };

  private renderPageNumbers = (): JSX.Element[] => {
    const VISIBLE_PAGES = this.props.visiblePages;
    const { steps, activeStep, showLastPage } = this.props;
    let items: JSX.Element[] = [];

    let renderPrevDots = false;
    let renderNextDots = false;
    let startIndex = 1;
    let endIndex = steps;
    if (steps > VISIBLE_PAGES) {
      renderPrevDots = true;
      renderNextDots = true;
      const offset = Math.floor(VISIBLE_PAGES / 2);
      let left = activeStep - offset;
      let right = activeStep + offset;
      // Adjust visible range for showing last page
      if (showLastPage && right < steps - 1) {
        // Reserve space for last page and dots if needed
        const visiblePagesAdjusted = VISIBLE_PAGES - (showLastPage ? 2 : 0);
        right = Math.min(left + visiblePagesAdjusted - 1, steps - 2);
      }
      if (left <= 1) {
        left = 1;
        right = Math.min(VISIBLE_PAGES, showLastPage ? steps - 2 : steps);
        renderPrevDots = false;
      }
      if (right >= steps) {
        right = steps;
        left = steps - VISIBLE_PAGES;
        renderNextDots = false;
      }

      if (renderPrevDots && left == 1) {
        renderPrevDots = false;
      }

      startIndex = left;
      endIndex = right;
    }

    if (this.props.alwaysShowFirstPage && startIndex > 1) {
      items.push(
        <li key={0} className="pagination__list-item">
          <a href="#" onClick={(e) => this.onChangeStep(1, e)} className="pagination__link">
            1
          </a>
        </li>
      );
    }
    if (renderPrevDots) {
      items.push(
        <li key={startIndex - 1} className="pagination__list-item">
          <a
            href="#"
            onClick={(e) => this.onChangeStep(startIndex - 1, e)}
            className="pagination__link pagination__link-dots"
          >
            ...
          </a>
        </li>
      );
    }

    for (let index = startIndex; index <= endIndex; index++) {
      if (index === activeStep) {
        items.push(
          <li
            key={index}
            className="pagination__list-item pagination__list-item--active"
          >
            <span className="pagination__link pagination__link--active">
              {index}
            </span>
          </li>
        );
      } else {
        items.push(
          <li key={index} className="pagination__list-item">
            <a
              href="#"
              onClick={(e) => this.onChangeStep(index, e)}
              className="pagination__link"
            >
              {index}
            </a>
          </li>
        );
      }
    }

    // Add ellipsis and last page if needed
    if (showLastPage && endIndex < steps - 1) {
      items.push(
        <li key="lastPageDots" className="pagination__list-item">
          <a
            href="#"
            onClick={(e) => this.onChangeStep(endIndex + 1, e)}
            className="pagination__link pagination__link-dots"
          >
            ...
          </a>
        </li>
      );

      // Add the last page
      items.push(
        <li key={steps} className="pagination__list-item">
          <a
            href="#"
            onClick={(e) => this.onChangeStep(steps, e)}
            className="pagination__link"
          >
            {steps}
          </a>
        </li>
      );
    } else if (renderNextDots) {
      items.push(
        <li key={endIndex + 1} className="pagination__list-item">
          <a
            href="#"
            onClick={(e) => this.onChangeStep(endIndex + 1, e)}
            className="pagination__link pagination__link-dots"
          >
            ...
          </a>
        </li>
      );
    }

    return items;
  };

  public render() {
    const {
      steps,
      nextLabel,
      previousLabel,
      activeStep,
      showPreviousOnFirstPage,
      showNextOnLastPage,
    } = this.props;
    const previousEnabled = activeStep !== 1;
    const nextEnabled = steps !== activeStep;
    var hidePrevious = previousEnabled ? "" : "hide-list-item";
    var hideNext = nextEnabled ? "" : "hide-list-item";
    return (
      <nav className="pagination" aria-label="Navigera mellan resultatsidor">
        <ol
          className={`pagination__list ${
            steps >= 5 ? "pagination__list--multiple-items" : ""
          }`}
        >
          <li
            className={`pagination__list-item ${hidePrevious} ${
              steps >= 5 ? "pagination__list-item--multiple-items" : ""
            }`}
          >
            <Previous
              label={previousLabel}
              isEnabled={previousEnabled}
              onClickCallback={this.onPrevious}
            />
          </li>

          {this.renderPageNumbers()}

          <li
            className={`pagination__list-item ${hideNext} ${
              steps >= 5 ? "pagination__list-item--multiple-items" : ""
            }`}
          >
            <Next
              label={nextLabel}
              isEnabled={nextEnabled}
              onClickCallback={this.onNext}
            />
          </li>
        </ol>
      </nav>
    );
  }
}
